import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { EnhancedJournalFilters } from '../../components/journals/EnhancedJournalFilters';
import { JournalEntryView } from '../../components/journals/JournalEntryView';
import { NewJournalEntryModal } from '../../components/journals/NewJournalEntryModal';
import { JournalEntryEditModal } from '../../components/journals/JournalEntryEditModal';
import { AgentEntryEditModal } from '../../components/journals/AgentEntryEditModal';
import { UnifiedJournalList } from '../../components/journals/UnifiedJournalList';
import { Modal } from '../../components/ui/Modal';
import { useJournalEntries } from '../../hooks/useJournalEntries';
import { useAgentEntries } from '../../hooks/useAgentEntries';
import { useEnhancedJournalFilters } from '../../hooks/useEnhancedJournalFilters';
import { useOrganization } from '../../hooks/useOrganization';
import { useJournalExport } from '../../hooks/useJournalExport';
import type { JournalEntry } from '../../types/accounting';

export function JournalsPage() {
  const [showNewEntryModal, setShowNewEntryModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAgentEditModal, setShowAgentEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const { entries, loading, addEntry, updateEntry, deleteEntry } = useJournalEntries();
  const { 
    entries: agentEntries, 
    loading: agentLoading, 
    validateEntry, 
    validateBatchEntries,
    updateEntry: updateAgentEntry 
  } = useAgentEntries();
  
  // Combiner toutes les entrées pour le filtrage avec protection renforcée
  const safeEntries = Array.isArray(entries) ? entries : [];
  const safeAgentEntries = Array.isArray(agentEntries) ? agentEntries : [];
  const allEntries = [...safeEntries, ...safeAgentEntries];
  
  // Vérification supplémentaire pour s'assurer qu'on a toujours un tableau
  if (!Array.isArray(allEntries)) {
    console.error('allEntries is not an array:', allEntries);
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600">Erreur: Données invalides</div>
      </div>
    );
  }
  
  // Utiliser les nouveaux filtres améliorés
  const {
    filters,
    updateFilters,
    resetFilters,
    filteredEntries,
    isFiltersActive,
    filterStats,
    getJournalTypeOptions,
    getStatusOptions,
    getSourceOptions
  } = useEnhancedJournalFilters(allEntries);
  
  // Hook pour les informations d'organisation (export)
  const { organization } = useOrganization();

  // Hook pour l'export
  const {
    loading: isExporting,
    error: exportError,
    handleExport: exportJournal
  } = useJournalExport();

  const handleNewEntry = async (data: Partial<JournalEntry>) => {
    try {
      await addEntry(data);
      setShowNewEntryModal(false);
    } catch (error) {
      console.error('Failed to add journal entry:', error);
    }
  };

  const handleView = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setShowViewModal(true);
  };

  const handleUploadAttachment = async (entry: JournalEntry, file: File) => {
    try {
      // Logique d'upload à implémenter
      console.log('Uploading attachment for entry:', entry.id, 'File:', file.name);
    } catch (error) {
      console.error('Failed to upload attachment:', error);
    }
  };

  const handleDownloadAttachment = async (entry: JournalEntry, attachmentId: string) => {
    try {
      const attachment = entry.attachments?.find(att => att.id === attachmentId);
      if (attachment) {
        // Logique de téléchargement à implémenter
        console.log('Downloading attachment:', attachment.name);
      }
    } catch (error) {
      console.error('Failed to download attachment:', error);
    }
  };

  const handleEdit = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setShowEditModal(true);
  };

  const handleDelete = async (entry: JournalEntry) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette écriture ?')) {
      try {
        await deleteEntry(entry.id);
      } catch (error) {
        console.error('Failed to delete journal entry:', error);
      }
    }
  };

  const handleUpdate = async (updatedEntry: JournalEntry) => {
    try {
      await updateEntry(updatedEntry.id, updatedEntry);
      setShowEditModal(false);
      setSelectedEntry(null);
    } catch (error) {
      console.error('Failed to update journal entry:', error);
    }
  };

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      await exportJournal({
        entries: filteredEntries,
        format,
        filters,
        organization,
        title: 'JOURNAL DES ÉCRITURES COMPTABLES'
      });
    } catch (error) {
      console.error(`Erreur lors de l'export ${format}:`, error);
    }
  };

  // const handleAgentEntryEdit = (entry: JournalEntry) => {
  //   setSelectedEntry(entry);
  //   setShowAgentEditModal(true);
  // };

  const handleAgentEntryUpdate = async (updatedEntry: JournalEntry) => {
    try {
      await updateAgentEntry(updatedEntry.id, updatedEntry);
      setShowAgentEditModal(false);
      setSelectedEntry(null);
    } catch (error) {
      console.error('Failed to update agent entry:', error);
    }
  };

  // const handleAgentEntryDelete = async (entry: JournalEntry) => {
  //   if (window.confirm('Êtes-vous sûr de vouloir supprimer cette écriture générée par l\'agent ?')) {
  //     try {
  //       await deleteAgentEntry(entry.id);
  //     } catch (error) {
  //       console.error('Failed to delete agent entry:', error);
  //     }
  //   }
  // };

  const handleValidateEntry = async (entry: JournalEntry) => {
    try {
      await validateEntry(entry.id);
    } catch (error) {
      console.error('Failed to validate entry:', error);
    }
  };

  const handleValidateBatchEntries = async (entries: JournalEntry[]) => {
    try {
      const entryIds = entries.map(entry => entry.id);
      await validateBatchEntries(entryIds);
    } catch (error) {
      console.error('Failed to validate batch entries:', error);
    }
  };

  // const handleValidateBatchEntries = async (entryIds: string[]) => {
  //   try {
  //     await validateBatchEntries(entryIds);
  //   } catch (error) {
  //     console.error('Failed to validate batch entries:', error);
  //   }
  // };

  // Pagination - séparer les entrées normales et ADHA selon le filtre source
  const normalEntries = filteredEntries.filter(entry => entry.source !== 'agent');
  const adhaEntries = filteredEntries.filter(entry => entry.source === 'agent');
  
  // Utiliser les bonnes entrées selon le filtre
  const entriesToPaginate = filters.source === 'agent' ? adhaEntries : 
                           filters.source === 'manual' ? normalEntries : 
                           filteredEntries;
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEntries = entriesToPaginate.slice(startIndex, endIndex);
  const totalPages = Math.ceil(entriesToPaginate.length / itemsPerPage);

  if (loading || agentLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec actions */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Journal des Écritures
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gestion et consultation des écritures comptables
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowNewEntryModal(true)}
            className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Nouvelle Écriture
          </Button>
        </div>
      </div>

      {/* Filtres améliorés */}
      <EnhancedJournalFilters
        filters={filters}
        onFiltersChange={updateFilters}
        onReset={resetFilters}
        filterStats={filterStats}
        isFiltersActive={isFiltersActive}
        journalTypeOptions={getJournalTypeOptions}
        statusOptions={getStatusOptions}
        sourceOptions={getSourceOptions}
      />

      {/* Message d'erreur d'export */}
      {exportError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Erreur d'export: {exportError}
          </p>
        </div>
      )}

      {/* Liste unifiée des écritures */}
      <UnifiedJournalList
        entries={paginatedEntries}
        agentEntries={agentEntries} 
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onValidate={handleValidateEntry}
        onValidateAll={handleValidateBatchEntries}
        onUploadAttachment={handleUploadAttachment}
        onDownloadAttachment={handleDownloadAttachment}
        onExport={handleExport}
        loading={isExporting}
        page={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* Modales */}
      {showNewEntryModal && (
        <NewJournalEntryModal
          isOpen={showNewEntryModal}
          onClose={() => setShowNewEntryModal(false)}
          onSubmit={handleNewEntry}
        />
      )}

      {showViewModal && selectedEntry && (
        <Modal 
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          title="Détails de l'écriture"
        >
          <JournalEntryView
            entry={selectedEntry}
          />
        </Modal>
      )}

      {showEditModal && selectedEntry && (
        <JournalEntryEditModal
          entry={selectedEntry}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdate}
        />
      )}

      {showAgentEditModal && selectedEntry && (
        <AgentEntryEditModal
          entry={selectedEntry}
          isOpen={showAgentEditModal}
          onClose={() => setShowAgentEditModal(false)}
          onSave={handleAgentEntryUpdate}
          onValidate={handleValidateEntry}
        />
      )}
    </div>
  );
}
